import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink } from "react-router";

import * as Api from "@/api";
import CountryFlag from "@/app/domain/callflow/timetables/holidaysets/calendar/CountryFlag";
import { NEW_PBX } from "@/constants/New";
import urls from "@/constants/Urls";
import Avatar from "@/design/components/Avatar";
import HeaderCard from "@/design/components/HeaderCard";
import ImageSelect from "@/design/components/ImageSelect";
import SaveIndicator from "@/design/components/SaveIndicator";
import FormGroup from "@/design/forms/FormGroup";
import InputAutocomplete from "@/design/forms/InputAutocomplete";
import InputCopy from "@/design/forms/InputCopy";
import InputNumeric from "@/design/forms/InputNumeric";
import InputSelect from "@/design/forms/InputSelect";
import InputText from "@/design/forms/InputText";
import InputToggle from "@/design/forms/InputToggle";
import StringForm from "@/design/forms/StringForm";
import CountryCodes from "@/resources/countrycode";
import { setPbx as setAppPbx } from "@/store/reducers/dataPbx";
import { number, string } from "@/utils/hooks/endorse";
import useSave, { SaveState } from "@/utils/hooks/useSave";
import Valkyrie, {
  viArrowLeft,
  viArrowsRotateRight,
  viBracketsCurly,
  viBuilding,
  viCircleInfo,
  viFloppyDisk,
  viKey,
  viPhoneGear,
  viPlus,
  viSimCard,
} from "@sippy-platform/valkyrie";

import {
  Alert,
  Box,
  Button,
  Card,
  Container,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormHelperText,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import pbxReducer from "./pbxReducer";
import TokenDialog from "./TokenDialog";

function Pbx() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const matches = useMediaQuery(theme.breakpoints.up("md"));

  const [tab, setTab] = useState(0);
  const [credit, setCredit] = useState(0);
  const [client, setClient] = useState("Zoiper");
  const [provisioningClient, setProvisioningClient] = useState("Yealink");
  const [billingCredentials, setBillingCredentials] = useState(null);

  const auth = useSelector((state) => state.auth);
  const dispatcherStore = useSelector((state) => state.system.dispatchers);
  const domainStore = useSelector((state) => state.domain);
  const phoneNumberStore = useSelector((state) => state.domain.pbx.phoneNumbers);
  const inboundRouteStore = useSelector((state) => state.domain.inboundRoutes);
  const blocklistStore = useSelector((state) => state.domain.blocklists);

  const outgoingNumberList = useMemo(() => {
    return (
      phoneNumberStore?.map((phoneNumber) => ({
        did: phoneNumber.did,
        name: inboundRouteStore.list.find((inbound) => inbound.did === phoneNumber.did)?.name || "",
      })) || []
    );
  }, [inboundRouteStore.list, phoneNumberStore]);

  // Yealink settings
  const [yealinkSettings, setYealinkSettings] = useState({});
  const formHandlerYealink = (property, value) => {
    setYealinkSettings((prev) => ({ ...prev, [property]: value }));
  };

  const saveYealinkSettings = () => {
    pbxDispatch({ type: "yealinkConfig", value: yealinkSettings });
    setYealinkSettings({});
    setSaveState(SaveState.UNSAVED);
  };

  // Fanvil settings
  const [fanvilSettings, setFanvilSettings] = useState({});
  const formHandlerFanvil = (property, value) => {
    setFanvilSettings((prev) => ({ ...prev, [property]: value }));
  };

  const saveFanvilSettings = () => {
    pbxDispatch({ type: "fanvilConfig", value: fanvilSettings });
    setFanvilSettings({});
    setSaveState(SaveState.UNSAVED);
  };

  // Get general PBX info
  const {
    value: pbx,
    dispatch: pbxDispatch,
    setSaveState,
    formState,
    errors,
    allErrors,
    save,
  } = useSave({
    initialValue: NEW_PBX,
    reducer: pbxReducer,
    validator: () => ({
      name: string().required({ message: "Name is required." }),
      maxSippeers: number().required({ message: "Maximum users is required." }),
      maxConcurrentLines: number().required({ message: "Maximum lines is required." }),
      zip: string().required({ message: "ZIP is required." }),
    }),
    onSubmit: () => Api.putPbx(pbx),
    onSuccess: () => dispatch(setAppPbx({ pbx })),
  });

  useEffect(() => {
    Promise.all([
      Api.getPbx(),
      Api.getPbxCredit(),
      Api.getPbxCredentials(),
      Api.getTokens(),
      Api.getDispatchers(),
      Api.getInbounds(),
      Api.getBlocklists(),
    ]).then((response) => {
      pbxDispatch({ type: "set", value: response[0].data.pbx });
      setYealinkSettings({});
      setFanvilSettings({});
      setCredit(response[1].data.credit);
      setBillingCredentials(response[2].data.billingCredentials);
    });
  }, [pbxDispatch]);

  // Handle changes
  const formHandler = (property, value) => {
    pbxDispatch({ type: property, value });
    setSaveState(SaveState.UNSAVED);
  };

  // Refresh provisioning key
  const [requestingKey, setRequestingKey] = useState();
  const [openKeyDialog, setOpenKeyDialog] = useState(false);

  function refreshProvisioningKey() {
    setOpenKeyDialog(false);
    setRequestingKey(true);

    Promise.resolve(Api.requestNewProvisioningKey())
      .then(() =>
        Promise.resolve(Api.getPbx()).then((response) => {
          setYealinkSettings("");
          setFanvilSettings("");
          pbxDispatch({ type: "set", value: response.data.pbx });
        }),
      )
      .finally(() => setRequestingKey(false));
  }

  // Manage tokens
  const [currentToken, setCurrentToken] = useState();

  return (
    <Container>
      <title>{`My PBX \u00b7 ${pbx.name} \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>
      <Stack
        sx={{
          gap: 2,
        }}
      >
        <HeaderCard
          tab={tab}
          changeTab={(e, newTab) => setTab(newTab)}
          tabs={[
            { label: "PBX", icon: viBuilding, id: "pbx" },
            { label: "Configuration", icon: viPhoneGear, id: "configuration" },
            { label: "Phone numbers", icon: viSimCard, id: "phonenumbers" },
            { label: "Developer", icon: viBracketsCurly, id: "developer" },
            { label: "Info", icon: viCircleInfo, id: "info" },
          ]}
          avatar={
            !matches ? (
              <IconButton variant="avatar" component={NavLink} to={"/my-pbx"}>
                <Valkyrie icon={viArrowLeft} />
                <Avatar text={pbx.name} icon={viBuilding} />
              </IconButton>
            ) : (
              <Avatar text={pbx.name} icon={viBuilding} />
            )
          }
          action={<SaveIndicator formState={formState} errors={allErrors} save={save} />}
          title={pbx.name || "Unnamed PBX"}
        />
        {tab === 0 && (
          <>
            {auth.accessLevel <= 1 ? (
              <>
                <FormGroup title="Identity">
                  <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                    <InputText required id="name" label="Name" onChange={formHandler} value={pbx.name} error={errors} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                    <InputText
                      required
                      id="zip"
                      label="Postal code"
                      onChange={formHandler}
                      value={pbx.zip}
                      error={errors}
                    />
                  </Grid>
                  {auth.accessLevel <= 1 && (
                    <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                      <InputText
                        id="customerReference"
                        label="Customer reference"
                        onChange={formHandler}
                        value={pbx.customerReference}
                        error={errors}
                      />
                    </Grid>
                  )}
                </FormGroup>
                <FormGroup title="Calls">
                  <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                    <InputAutocomplete
                      required
                      id="defaultCountryCallingCode"
                      label="Default country calling code"
                      onChange={formHandler}
                      value={`+${pbx.defaultCountryCallingCode}`}
                      selectValue="phone"
                      selects={CountryCodes}
                      getOptionLabel={(option) => `${option.phone} \u00b7 ${option.name}`}
                      renderOptionLabel={(option) => (
                        <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
                          <CountryFlag code={option.code} />
                          <span>{`${option.phone} \u00b7 ${option.name}`}</span>
                        </Stack>
                      )}
                      error={errors}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                    <InputAutocomplete
                      required
                      id="defaultPhoneNumber"
                      label="Default phone number"
                      onChange={formHandler}
                      value={pbx.defaultPhoneNumber}
                      selectValue="did"
                      selects={outgoingNumberList}
                      getOptionLabel={(option) => (option.name ? `${option.did} \u00b7 ${option.name}` : option.did)}
                      renderOptionLabel={(option) => (
                        <>
                          {option.did}
                          {option.name !== "" && <span style={{ opacity: 0.6 }}>&nbsp;&middot; {option.name}</span>}
                        </>
                      )}
                      error={errors}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                    <InputAutocomplete
                      hasEmpty={null}
                      id="globalBlocklistId"
                      label="Global blocklist"
                      onChange={formHandler}
                      value={pbx.globalBlocklistId}
                      selectValue="id"
                      selects={blocklistStore.list.filter((blocklist) => blocklist.type === "pbx")}
                      getOptionLabel={(option) => option.name}
                      error={errors}
                    />
                  </Grid>
                </FormGroup>
                <FormGroup title="Usage limits">
                  <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                    <InputNumeric
                      required
                      id="maxSippeers"
                      label="Maximum users"
                      onChange={formHandler}
                      min={1}
                      value={pbx.maxSippeers}
                      error={errors}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                    <InputNumeric
                      required
                      id="maxConcurrentLines"
                      label="Maximum lines"
                      onChange={formHandler}
                      min={1}
                      value={pbx.maxConcurrentLines}
                      error={errors}
                    />
                  </Grid>
                </FormGroup>
                <FormGroup title="Personalization">
                  <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                    <ImageSelect
                      id="wallpaper"
                      value={pbx.wallpaper?.id}
                      select={(value) => formHandler("wallpaper", value)}
                      label="Default wallpaper"
                      error={errors}
                      helpeTestr="Set the default wallpaper for all devices in this PBX with support for wallpapers. Devices that use the default wallpaper have to sync before the change takes effect."
                      defaultLabel="System default"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                    <ImageSelect
                      id="screensaver"
                      value={pbx.screensaver?.id}
                      select={(value) => formHandler("screensaver", value)}
                      label="Default screensaver"
                      error={errors}
                      helpeTestr="Set the default screensaver for all devices in this PBX with support for screensavers. Devices that use the default screensaver have to sync before the change takes effect."
                      defaultLabel="System default"
                    />
                  </Grid>
                </FormGroup>
                <FormGroup title="AI">
                  <Grid size={{ xs: 12 }}>
                    <InputToggle
                      id="enableAi"
                      error={errors}
                      onChange={formHandler}
                      states={[false, true]}
                      value={pbx.enableAi}
                      primary="Analyze calls to extract insights and transcripts"
                      secondary="When a call is finished, Sippy will analyze the recording with AI to create a transcript, extract insights such as sentiment, a summary of the call and the language of the call. This information will show up in the Call Log. In order for a call to be analyzed, it must be recorded. Recordings are always stored on servers within the EU for 60 days. The analysis of the recording also happens within the EU and does not store the recording. AI analysis cannot be retroactively applied."
                    />
                  </Grid>
                </FormGroup>
              </>
            ) : (
              <FormGroup title="Identity">
                <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                  <StringForm label="Name" text={pbx.name} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                  <StringForm label="Postal code" text={pbx.zip} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                  <StringForm label="Default phone number" text={pbx.defaultPhoneNumber || "None"} />
                </Grid>
              </FormGroup>
            )}
            {auth.accessLevel > 1 && domainStore.accounts?.length > 0 && (
              <FormGroup title="Accounts">
                <Grid size={12}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(min(16rem, 100%), 1fr))",
                      gap: 1,
                    }}
                  >
                    {domainStore.accounts?.map((account) => (
                      <Card variant="outlined" key={account.id}>
                        <List>
                          <ListItem>
                            <ListItemAvatar>
                              <Avatar text={`${account.firstname} ${account.lastname ?? ""}`} />
                            </ListItemAvatar>
                            <ListItemText
                              primary={`${account.firstname} ${account.lastname ?? ""}`}
                              secondary={account.email}
                            />
                          </ListItem>
                        </List>
                      </Card>
                    ))}
                  </Box>
                </Grid>
              </FormGroup>
            )}
            <FormGroup title="About">
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <StringForm
                  label="Credit"
                  text={`€${parseFloat(Math.round(credit * 100) / 100).toFixed(2)}`}
                  action={
                    <form
                      action="https://billing.optimisecloud.be/a2billing/customer/userinfo.php"
                      method="POST"
                      target="_blank"
                    >
                      {billingCredentials && (
                        <>
                          <input type="hidden" name="done" value="submit_log" />
                          <input type="hidden" name="pr_login" value={billingCredentials.useralias} />
                          <input type="hidden" name="pr_password" value={billingCredentials.uipass} />
                        </>
                      )}
                      <Button type="submit" variant="contained">
                        More info
                      </Button>
                    </form>
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <StringForm label="Context" text={pbx.context} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <StringForm label="Account code" text={pbx.accountcode} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <StringForm label="Partner" text={pbx.partner?.name} />
              </Grid>
            </FormGroup>
          </>
        )}
        {tab === 1 && (
          <>
            {auth.accessLevel <= 1 ? (
              <>
                <FormGroup title="Yealink custom configuration">
                  <Grid size={12}>
                    <Alert severity="warning">
                      Configuring Yealink features through this interface will overwrite any existing configurations
                      officially supported by Sippy. Note that custom configuration set in user settings will overwrite
                      the configuration set on the PBX level. Only use this at your own risk. Yealink custom
                      configuration won't save automatically to make sure you don't accidentally push an incorrect
                      configuration, which may cause the device to not accept any further changes you make in these
                      settings.
                    </Alert>
                  </Grid>
                  <Grid size={12}>
                    <InputText
                      id="yealinkConfig"
                      label="Yealink configuration"
                      onChange={formHandlerYealink}
                      value={yealinkSettings.yealinkConfig ?? pbx.yealinkConfig}
                      error={errors}
                      multiline
                      rows={8}
                      sx={{
                        "& .MuiInputBase-input": {
                          fontFamily: "monospace",
                        },
                      }}
                    />
                  </Grid>
                  <Grid style={{ display: "flex", justifyContent: "end" }} size={12}>
                    <Button
                      variant="contained"
                      onClick={saveYealinkSettings}
                      disabled={Object.keys(yealinkSettings).length === 0}
                      startIcon={<Valkyrie icon={viFloppyDisk} />}
                    >
                      Save
                    </Button>
                  </Grid>
                </FormGroup>
                <FormGroup title="Fanvil custom configuration">
                  <Grid size={12}>
                    <Alert severity="warning">
                      Configuring Fanvil features through this interface will overwrite any existing configurations
                      officially supported by Sippy. Note that custom configuration set in user settings will overwrite
                      the configuration set on the PBX level. Only use this at your own risk. Fanvil custom
                      configuration won't save automatically to make sure you don't accidentally push an incorrect
                      configuration, which may cause the device to not accept any further changes you make in these
                      settings.
                    </Alert>
                  </Grid>
                  <Grid size={12}>
                    <InputText
                      id="fanvilConfig"
                      label="Fanvil configuration"
                      onChange={formHandlerFanvil}
                      value={fanvilSettings.fanvilConfig ?? pbx.fanvilConfig}
                      error={errors}
                      multiline
                      rows={8}
                      sx={{
                        "& .MuiInputBase-input": {
                          fontFamily: "monospace",
                        },
                      }}
                    />
                  </Grid>
                  <Grid style={{ display: "flex", justifyContent: "end" }} size={12}>
                    <Button
                      variant="contained"
                      onClick={saveFanvilSettings}
                      disabled={Object.keys(fanvilSettings).length === 0}
                      startIcon={<Valkyrie icon={viFloppyDisk} />}
                    >
                      Save
                    </Button>
                  </Grid>
                </FormGroup>
              </>
            ) : (
              <FormGroup title="Custom device configuration">
                <Grid size={12}>
                  <Alert severity="info">You don't have access to custom device configuration settings.</Alert>
                </Grid>
              </FormGroup>
            )}
          </>
        )}
        {tab === 2 && (
          <FormGroup title="Phone numbers">
            <Grid size={12}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(min(16rem, 100%), 1fr))",
                  gap: 1,
                }}
              >
                {outgoingNumberList.map((number) => (
                  <Card variant="outlined" key={number.did}>
                    <List>
                      <ListItem>
                        <ListItemAvatar>
                          <Avatar text={number.did} icon={viSimCard} px={32} />
                        </ListItemAvatar>
                        <ListItemText primary={number.did} secondary={number.name} />
                      </ListItem>
                    </List>
                  </Card>
                ))}
              </Box>
            </Grid>
          </FormGroup>
        )}
        {tab === 3 && (
          <FormGroup
            title="Tokens"
            actions={
              <Button variant="contained" startIcon={<Valkyrie icon={viPlus} />} onClick={() => setCurrentToken("new")}>
                Add
              </Button>
            }
          >
            {currentToken && <TokenDialog closeDialog={() => setCurrentToken(undefined)} tokenId={currentToken} />}
            <Grid size={12}>
              <Stack spacing={1}>
                {domainStore.apiTokens.list.length >= 1 ? (
                  domainStore.apiTokens.list.map((token) => (
                    <Card key={token.id} variant="outlined">
                      <List>
                        <ListItem sx={{ p: 0 }}>
                          <ListItemButton
                            sx={{ borderRadius: 0 }}
                            onClick={() => setCurrentToken(token.id)}
                            id={`token${token.id}`}
                          >
                            <ListItemAvatar>
                              <Avatar text={token.name} icon={viKey} />
                            </ListItemAvatar>
                            <ListItemText primary={token.name} secondary={token.description} />
                          </ListItemButton>
                        </ListItem>
                      </List>
                    </Card>
                  ))
                ) : (
                  <Card variant="outlined">
                    <List>
                      <ListItem sx={{ p: 0 }}>
                        <ListItemButton sx={{ pr: "80px !important" }} onClick={() => setCurrentToken("new")}>
                          <ListItemIcon>
                            <Valkyrie icon={viPlus} />
                          </ListItemIcon>
                          <ListItemText primary="Create your first token" />
                        </ListItemButton>
                      </ListItem>
                    </List>
                  </Card>
                )}
              </Stack>
            </Grid>
          </FormGroup>
        )}
        {tab === 4 && (
          <>
            <FormGroup title="Network information">
              <Grid size={{ xs: 6, lg: 4 }}>
                <StringForm
                  label="Voice services"
                  text={dispatcherStore.list.map((dispatcher) => (
                    <Typography key={dispatcher.id} variant="body2" sx={{ mt: -0.25, display: "block", mb: 0.25 }}>
                      {dispatcher.destination.split(":")[1]}
                    </Typography>
                  ))}
                />
              </Grid>
              <Grid size={{ xs: 6, lg: 4 }}>
                <StringForm label="SIP domain" text="voip.optimisecloud.be" />
              </Grid>
              <Grid size={{ xs: 6, lg: 4 }}>
                <StringForm label="SIP IP" text="18.194.213.102" />
              </Grid>
            </FormGroup>
            <FormGroup title="Phones">
              <Grid size={{ xs: 12 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ pl: 0 }}>Protocol</TableCell>
                      <TableCell>Port</TableCell>
                      <TableCell>Destination</TableCell>
                      <TableCell sx={{ pr: 0 }}>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ pl: 0 }}>UDP</TableCell>
                      <TableCell>5060</TableCell>
                      <TableCell>Any</TableCell>
                      <TableCell sx={{ pr: 0 }}>SIP</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ pl: 0 }}>UDP</TableCell>
                      <TableCell>10000-30000</TableCell>
                      <TableCell>Any</TableCell>
                      <TableCell sx={{ pr: 0 }}>RTP</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ pl: 0 }}>TCP</TableCell>
                      <TableCell>443</TableCell>
                      <TableCell>provisioning.sippy.cloud</TableCell>
                      <TableCell sx={{ pr: 0 }}>Provisioning</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Grid>
            </FormGroup>
            <FormGroup title="CloudSoftphone">
              <Grid size={{ xs: 12 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ pl: 0 }}>Protocol</TableCell>
                      <TableCell>Port</TableCell>
                      <TableCell>Destination</TableCell>
                      <TableCell sx={{ pr: 0 }}>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ pl: 0 }}>UDP/TCP</TableCell>
                      <TableCell>4998</TableCell>
                      <TableCell>Any</TableCell>
                      <TableCell sx={{ pr: 0 }}>SIP</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ pl: 0 }}>TCP</TableCell>
                      <TableCell>24998</TableCell>
                      <TableCell>Any</TableCell>
                      <TableCell sx={{ pr: 0 }}>Push notifications</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ pl: 0 }}>UDP</TableCell>
                      <TableCell>443</TableCell>
                      <TableCell>Any</TableCell>
                      <TableCell sx={{ pr: 0 }}>Provisioning</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Grid>
            </FormGroup>
            <FormGroup title="Internal phonebook">
              <Grid size={12}>
                <Stack
                  direction="row"
                  sx={{
                    alignItems: "stretch",
                    gap: 1,
                    height: 40,
                  }}
                >
                  <InputSelect
                    id="client"
                    label="Client"
                    error={errors}
                    onChange={(_, value) => setClient(value)}
                    value={client}
                    selects={[
                      { value: "Fanvil" },
                      { value: "Gigaset" },
                      { value: "Snom" },
                      { value: "Yealink" },
                      { value: "Zoiper" },
                    ]}
                    getOptionLabel={(option) => option.value}
                    sx={{ width: 180 }}
                  />
                  <InputCopy
                    id="phonebookUrl"
                    label="Phonebook URL"
                    value={`${urls.phonebook}?id=${pbx.context}&client=${client.toLowerCase()}&token=${pbx.internalPhonebookToken}`}
                  />
                </Stack>
              </Grid>
            </FormGroup>
            <FormGroup
              title="Provisioning"
              actions={
                <Button
                  startIcon={<Valkyrie icon={viArrowsRotateRight} spin={requestingKey} />}
                  onClick={() => setOpenKeyDialog(true)}
                  variant="contained"
                >
                  Refresh key
                </Button>
              }
            >
              <Grid size={12}>
                <Stack
                  direction="row"
                  sx={{
                    alignItems: "stretch",
                    gap: 1,
                    height: 40,
                  }}
                >
                  <InputSelect
                    id="client"
                    label="Client"
                    error={errors}
                    onChange={(_, value) => setProvisioningClient(value)}
                    value={provisioningClient}
                    selects={pbx.provisioningUrls ?? []}
                    selectValue="client"
                    getOptionLabel={(option) => option.client}
                    sx={{ width: 180 }}
                  />
                  <InputCopy
                    id="provisioningUrl"
                    label="Provisioning URL"
                    value={pbx.provisioningUrls?.find((_urls) => _urls.client === provisioningClient)?.url}
                  />
                </Stack>
                <FormHelperText>Paste this url in your device to start provisioning it.</FormHelperText>
              </Grid>
              <Dialog open={openKeyDialog} onClose={() => setOpenKeyDialog(false)}>
                <Stack
                  sx={{
                    gap: 2,
                  }}
                >
                  <DialogTitle sx={{ p: 0 }}>Are you sure you want to refresh your provisioning key?</DialogTitle>
                  <DialogContent sx={{ p: 0 }}>
                    <DialogContentText>
                      Requesting a new provisioning key will invalidate the previous one. You will need to replace the
                      URL in the device, otherwise devices won't be able to retrieve future updates to their
                      configuration. This can be done by setting the URL in the device itself.
                    </DialogContentText>
                    <DialogContentText sx={{ mt: 1 }}>
                      If you are using a Yealink device, this can be done from the Device Management Platform from
                      Yealink. Devices that are managed by the Device Management Platform from Yealink need to be reset
                      in order the retrieve the new URL.
                    </DialogContentText>
                  </DialogContent>
                  <Stack
                    direction="row"
                    sx={{
                      justifyContent: "flex-end",
                      gap: 1,
                    }}
                  >
                    <Button variant="outlined" onClick={() => setOpenKeyDialog(false)}>
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      onClick={refreshProvisioningKey}
                      autoFocus
                      startIcon={<Valkyrie icon={viArrowsRotateRight} spin={requestingKey} />}
                    >
                      Refresh key
                    </Button>
                  </Stack>
                </Stack>
              </Dialog>
            </FormGroup>
          </>
        )}
      </Stack>
    </Container>
  );
}

export default Pbx;
